/**
 * Sessão ADT e duração, num lugar só (S-064 onda 2).
 *
 * Duas divergências que motivaram este módulo:
 *
 * **O limiar de aviso.** O `AdtSessionPanel` avisava em 5 minutos e a S-064 propôs 10.
 * Dois números para o mesmo conceito, em telas que o mesmo dev olha na mesma sessão, é
 * como um deles fica errado sem ninguém notar. Aqui há um só, e a strip do board e o
 * card da overview leem o mesmo.
 *
 * **O formato da duração.** A trilha de correção formata segundos com uma casa; a
 * matriz de fases precisava do mesmo, e cada componente com o seu produziria "9.8s"
 * num canto e "9800ms" no outro.
 *
 * **O formato do custo.** Esta divergiu de verdade e apareceu na tela: no mesmo painel,
 * a faixa mostrava `$0.0090` e o card do run mostrava `US$ 0.01` — o mesmo número,
 * dois valores. Duas casas arredondam para cima uma correção que custou nove milésimos
 * de dólar, e num pacote inteiro o erro acumula na direção errada.
 *
 * 🔶 **Por que 10 e 3.** Uma rodada de deploy+gates de um objeto leva ~25s medidos, e um
 * pacote de quatro objetos ~90s. Dez minutos é folga para o pacote inteiro mais a
 * reconexão; três é o ponto em que só dá tempo de reconectar. Avisar em 5, como estava,
 * cabe um pacote pequeno e não cabe um médio.
 */

/** Aviso: ainda dá tempo de terminar o que está em voo e reconectar depois. */
export const SESSION_WARN_MIN = 10;
/** Alerta: só dá tempo de reconectar. Não comece nada novo. */
export const SESSION_ALERT_MIN = 3;

export type SessionUrgency = "ok" | "warn" | "alert" | "gone";

/** Minutos até expirar, ou `null` sem data. Nunca negativo: expirado é zero. */
export function minutesUntil(iso?: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (Number.isNaN(ms)) return null;
  return ms > 0 ? Math.round(ms / 60_000) : 0;
}

/**
 * Urgência da sessão, que é o que decide a cor e se a strip pede ação.
 *
 * Status que não seja `connected` é `gone` independentemente dos minutos: sessão
 * expirada com `expires_at` no futuro (o SAP derrubou antes da hora) não pode aparecer
 * como tranquila.
 */
export function sessionUrgency(
  status: string | undefined,
  minutes: number | null,
): SessionUrgency {
  if (status !== "connected") return "gone";
  if (minutes === null) return "ok";
  if (minutes <= SESSION_ALERT_MIN) return "alert";
  if (minutes <= SESSION_WARN_MIN) return "warn";
  return "ok";
}

/**
 * Duração legível a partir de milissegundos.
 *
 * Segundos com uma casa em toda a faixa: o gate de sintaxe leva ~200ms e o ATC ~10s, e
 * trocar de unidade no meio da coluna faz a comparação visual mentir. Acima de um
 * minuto vira `m:ss`, porque "94.3s" ninguém lê como minuto e meio.
 */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined) return null;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Tempo decorrido entre duas marcas ISO, em ms. `null` se faltar o começo. */
export function elapsedMs(
  startedAt?: string | null,
  finishedAt?: string | null,
  now: number = Date.now(),
): number | null {
  if (!startedAt) return null;
  const startMs = new Date(startedAt).getTime();
  if (Number.isNaN(startMs)) return null;
  const endMs = finishedAt ? new Date(finishedAt).getTime() : now;
  if (Number.isNaN(endMs)) return null;
  return Math.max(0, endMs - startMs);
}

/**
 * Custo em dólares, com quatro casas.
 *
 * Quatro e não duas: uma correção custa frações de centavo (✅ $0.009045 medido no
 * ensaio de 19/08), e `toFixed(2)` a arredonda para `$0.01`, exagerando em 10%. Num
 * pacote de quarenta objetos o erro acumula sempre na mesma direção.
 *
 * `null` não é zero: é "não chamou o modelo". Quem chama decide se omite.
 */
export function formatCost(usd: number | null | undefined): string | null {
  if (usd === null || usd === undefined) return null;
  return `$${usd.toFixed(4)}`;
}

/** Para onde pedir que o SAP devolva o reentrance ticket (S-070).
 *
 * O SAP só aceita `localhost` como destino do redirect, e recusa qualquer host público
 * com `SADT_RESOURCE 034 Invalid redirect URL`. Rodando o myGROW na máquina do dev, o
 * `localhost` coincide com o próprio myGROW e o callback direto funciona. Publicado não
 * coincide, e o retorno precisa cair em loopback para a extensão adt-connect encaminhar.
 *
 * 🔶 **Na dúvida, `localhost`.** Errar para `direct` num ambiente publicado produz
 * `Invalid redirect URL` na tela do SAP, sem pista da causa; errar para `localhost` na
 * máquina do dev apenas pede a extensão, que é um recado acionável.
 */
export function callbackMode(hostname: string): "direct" | "localhost" {
  return hostname === "localhost" || hostname === "127.0.0.1" ? "direct" : "localhost";
}

// Mora aqui, e não no EscalationPanel onde nasceu (#593): aquele painel deixou de
// usá-la quando o bloco de sessão saiu de lá, e helper exportado de um componente que
// não o consome obriga outro componente a importar de um componente. O lugar dela é ao
// lado do `sessionUrgency`, que é de quem ela deriva.
/** Se ainda dá tempo de a resposta valer alguma coisa (#577).
 *
 * ✅ **Medido ao vivo em 26/08.** A sessão ADT dura ~30 min e é a credencial com que o
 * run escreve no alvo. A pergunta ficou aberta 26 minutos e foi respondida com **57
 * segundos** de sessão sobrando. Quem respondeu não tinha como saber: o relógio existe
 * na faixa do maestro, em outro lugar da página, e este card não repetia nem alertava.
 *
 * `gone` quando o status não é `connected`, independentemente dos minutos: o SAP pode
 * derrubar antes da hora, e sessão expirada com `expires_at` no futuro não pode
 * aparecer como tranquila. É a mesma regra do `sessionUrgency`.
 */
export function answerWindow(
  status: string | undefined,
  minutesLeft: number | null,
): "ok" | "tight" | "gone" {
  // ⚠️ **Consome o `sessionUrgency` em vez de reimplementar a faixa.** A primeira versão
  // olhava só o `SESSION_ALERT_MIN`, então sessão com 6 minutos e pergunta aberta caía
  // em "ok": relógio neutro, sem aviso e sem reconectar, na faixa que a própria lib
  // documenta como curta demais. Dois números para o mesmo conceito é como um deles fica
  // errado sem ninguém notar, e o cabeçalho daquele módulo avisa exatamente disso.
  const urgency = sessionUrgency(status, minutesLeft);
  if (urgency === "gone") return "gone";
  return urgency === "ok" ? "ok" : "tight";
}
